import { ChevronDown, ChevronUp } from 'lucide-react'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useAuthStore } from '@caradasstore/AuthStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore/UserStore'
import { AdasBox, AdasPopper, AdasTypography } from '@components/wrapper-components'
import { useHistory } from 'react-router-dom'
import { getCalibrationCenter, refreshAuth } from '../../../api/api'
import { getCachedCalibrationCenters } from '../../../utils/calibrationCenterCache'
import { Logger } from '../../../logger'
import './CalibrationCenterBar.css'

const isCenterActive = (cc) => cc?.id != null && cc.is_active !== false && cc.active !== false

const dedupeById = (centers) => Array.from(new Map(centers.map((cc) => [cc.id, cc])).values())

export const CalibrationCenterBar = ({ centerDetail, setCenterDetail, isSideBarOpen = true, inline = false }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const triggerRef = useRef(null)
  const history = useHistory()
  const accessibleCcIds = useCenterStore((store) => store.accessibleCcIds)
  const isCenterAdminFlag = useCenterStore((store) => store.isCenterAdmin)
  const centerSummaries = useCenterStore((store) => store.centerSummaries)
  const centerListLoaded = useCenterStore((store) => store.centerListLoaded)
  const setAccessibleCcIds = useCenterStore((store) => store.setAccessibleCcIds)
  const setIsCenterAdmin = useCenterStore((store) => store.setIsCenterAdmin)
  const upsertCenterSummaries = useCenterStore((store) => store.upsertCenterSummaries)
  const authLogin = useAuthStore((store) => store.login)
  // System admins (UserStore.currentUser.is_admin) should also see the full list, even when
  // the auth response's isAdmin flag (mirrored in CenterStore.isCenterAdmin) wasn't set —
  // otherwise they fall through to the non-admin path and only see ccidOwned/Managed/Tech.
  const isSystemAdmin = useUserStore((store) => !!store.currentUser?.is_admin)
  const isCenterAdmin = isCenterAdminFlag || isSystemAdmin

  const handleCCSwitch = async (cc) => {
    try {
      const ccResponse = await getCalibrationCenter({ ccid: cc.id })
      // The global auth interceptor swallows non-401/403 errors and resolves
      // with undefined, so guard the response before reading .status.
      if (ccResponse?.status === 200) {
        setCenterDetail(ccResponse.data)
        history.push('/')
      }
    } catch {
      /* swallow — Logger already captured this in the api wrapper */
    } finally {
      setAnchorEl(null)
    }
  }

  // Derive the dropdown list from the cached summaries instead of refetching every mount.
  // For non-admins we filter the cache by their accessible IDs; admins see every cached center.
  const ccList = useMemo(() => {
    const all = Object.values(centerSummaries || {}).filter(isCenterActive)
    const scoped = isCenterAdmin ? all : all.filter((cc) => Array.isArray(accessibleCcIds) && accessibleCcIds.includes(cc.id))
    return dedupeById(scoped).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [centerSummaries, accessibleCcIds, isCenterAdmin])

  // Hydrate the dropdown:
  //   - The authoritative full list is loaded by PageLayout's bootstrap (CAR-3630),
  //     so admins simply read it from centerSummaries — nothing to fetch here.
  //   - Non-admin: backfill ONLY the accessible IDs that aren't in the summaries yet.
  //   - Non-admin & no accessibleCcIds yet → refreshAuth() to recover them, then backfill.
  //
  // We deliberately do NOT gate the store updates on a per-effect `cancelled` flag — the sidebar
  // conditionally renders this component on hover, so unhovering mid-fetch would otherwise discard
  // the response. The store update is safe whether or not the component is still mounted.
  useEffect(() => {
    // Hydrate from the session-storage cache for instant paint. Unlike before, this
    // does NOT mark the list as "loaded": a partial cache must not suppress the
    // authoritative full-list fetch (now owned by PageLayout's bootstrap).
    const cached = getCachedCalibrationCenters()
    if (cached.length > 0) upsertCenterSummaries(cached)

    const fetchMissingByIds = (ids) => {
      const missing = ids.filter((id) => id != null && !centerSummaries[id])
      if (missing.length === 0) return
      Promise.all(
        missing.map((ccid) =>
          getCalibrationCenter({ ccid })
            .then((res) => (res?.status === 200 ? res.data : null))
            .catch((error) => {
              Logger.error({ message: error, payload: { file: 'CalibrationCenterBar', method: 'getCalibrationCenter', ccid } })
              return null
            })
        )
      ).then((results) => {
        const ok = results.filter(Boolean)
        if (ok.length) upsertCenterSummaries(ok)
      })
    }

    // Admins read the full list from PageLayout's bootstrap (CAR-3630); nothing to fetch here.
    if (isCenterAdmin) return

    if (accessibleCcIds && accessibleCcIds.length > 0) {
      fetchMissingByIds(accessibleCcIds)
      return
    }

    refreshAuth()
      .then((res) => {
        if (res?.status !== 200) return
        const data = res.data || {}
        if (data.token && data.refreshToken) authLogin(data.token, data.refreshToken)
        const ids = Array.from(
          new Set([...(data.ccidOwned || []), ...(data.ccidManaged || []), ...(data.ccidTech || []), data.ccid].filter((id) => id != null))
        )
        setAccessibleCcIds(ids)
        setIsCenterAdmin(!!data.isAdmin)
        // Admin full list is handled by PageLayout's bootstrap; non-admins backfill their centers.
        if (!data.isAdmin && ids.length > 0) fetchMissingByIds(ids)
      })
      .catch((error) => {
        Logger.error({ message: error, payload: { file: 'CalibrationCenterBar', method: 'refreshAuth' } })
      })
    // centerSummaries intentionally omitted from deps — we don't want to refetch every time
    // a summary lands; otherwise we'd loop. The missing-id check inside fetchMissingByIds
    // re-evaluates the cache fresh on each effect run.
  }, [accessibleCcIds, isCenterAdmin]) // eslint-disable-line

  const handleOpen = (evt) => {
    // Keep the switcher inert until the full center list has loaded (CAR-3630).
    if (!centerListLoaded) return
    setAnchorEl(evt.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)

  useEffect(() => {
    if (!open) return undefined
    const handleMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return
      const popper = document.querySelector('.calibration-center-bar__menu, .calibration-center-bar__menu--inline')
      if (popper && popper.contains(e.target)) return
      handleClose()
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])
  const hasCenters = ccList.length > 0

  const menuContent = (
    <>
      <AdasBox className='calibration-center-bar__menu-header'>SWITCH LOCATION</AdasBox>
      {hasCenters ? (
        <AdasBox className='calibration-center-bar__menu-list'>
          {ccList.map((cc) => {
            const isSelected = cc.id === centerDetail?.id
            return (
              <AdasBox
                key={cc.id}
                component='button'
                type='button'
                onClick={() => handleCCSwitch(cc)}
                className={`calibration-center-bar__menu-item ${isSelected ? 'is-selected' : ''}`}
              >
                <span className='calibration-center-bar__menu-item-label'>{cc.name}</span>
                {isSelected && <span className='calibration-center-bar__menu-item-dot' aria-hidden='true' />}
              </AdasBox>
            )
          })}
        </AdasBox>
      ) : (
        <AdasTypography className='calibration-center-bar__menu-empty'>No other centers available</AdasTypography>
      )}
    </>
  )

  return (
    <AdasBox className='calibration-center-bar'>
      <AdasBox
        ref={triggerRef}
        component='button'
        type='button'
        onClick={handleOpen}
        disabled={!centerListLoaded}
        className={`calibration-center-bar__trigger ${centerListLoaded ? 'is-interactive' : 'is-disabled'} ${open ? 'is-open' : ''} ${
          !isSideBarOpen ? 'is-collapsed' : ''
        }`}
      >
        {isSideBarOpen && (
          <span className='calibration-center-bar__label-wrap'>
            <span className='calibration-center-bar__label'>{centerDetail?.name || 'Select center'}</span>
          </span>
        )}
        {open ? (
          <ChevronUp size={16} className='calibration-center-bar__chevron' />
        ) : (
          <ChevronDown size={16} className='calibration-center-bar__chevron' />
        )}
      </AdasBox>
      {inline ? (
        // The mobile drawer (AdasDrawer/vaul) is a modal layer that sets
        // `pointer-events: none` on <body> while open, re-enabling it only inside
        // the drawer's own content. AdasPopper portals to document.body, so its
        // taps were silently swallowed and fell through to whatever nav item sat
        // behind it (CAR-3786). Rendering inline — a normal descendant of the
        // drawer content, in document flow — sidesteps the portal entirely.
        open && <AdasBox className='calibration-center-bar__menu--inline'>{menuContent}</AdasBox>
      ) : (
        <AdasPopper open={open} anchorEl={anchorEl} placement='bottom-start' className='popper-top calibration-center-bar__menu'>
          {menuContent}
        </AdasPopper>
      )}
    </AdasBox>
  )
}

CalibrationCenterBar.propTypes = {
  centerDetail: PropTypes.object,
  setCenterDetail: PropTypes.func,
  isSideBarOpen: PropTypes.bool,
  inline: PropTypes.bool
}
